import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Star, Quote, Send, MessageSquare } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ReviewsPage() {
  const [reviews, setReviews] = useState([]);
  const [services, setServices] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    customer_name: "",
    rating: 5,
    review_text: "",
    service_type: ""
  });

  useEffect(() => {
    document.title = "Customer Reviews & Testimonials | Virgil's Heating & Air";
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute("content", "Read customer reviews and testimonials for Virgil's Heating & Air. 5-star rated HVAC services in Greater Sacramento. See why customers trust us for AC, heating, and mini split services.");
    }
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [reviewsRes, servicesRes] = await Promise.all([
          axios.get(`${API}/reviews`),
          axios.get(`${API}/services`)
        ]);
        setReviews(reviewsRes.data);
        setServices(servicesRes.data);
      } catch (e) {
        console.error("Error fetching data:", e);
      }
    };
    fetchData();
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleRatingChange = (rating) => {
    setFormData(prev => ({ ...prev, rating }));
  };

  const handleServiceChange = (value) => {
    setFormData(prev => ({ ...prev, service_type: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.customer_name || !formData.review_text || !formData.service_type) {
      toast.error("Please fill in all required fields");
      return;
    }

    setIsSubmitting(true);
    try {
      await axios.post(`${API}/reviews`, formData);
      toast.success("Thank you! Your review has been submitted for approval.");
      setFormData({
        customer_name: "",
        rating: 5,
        review_text: "",
        service_type: ""
      });
      setShowForm(false);
    } catch (e) {
      console.error("Error submitting review:", e);
      toast.error("Failed to submit review. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Calculate average rating
  const avgRating = reviews.length > 0 
    ? (reviews.reduce((sum, r) => sum + r.rating, 0) / reviews.length).toFixed(1)
    : 0;

  return (
    <div className="pt-36 min-h-screen bg-[#050505]" data-testid="reviews-page">
      {/* Hero */}
      <section className="relative py-24 overflow-hidden bg-[#0a0a0a]" data-testid="reviews-hero">
        <div
          className="absolute inset-0 bg-cover bg-top"
          style={{
            backgroundImage: "url(https://customer-assets.emergentagent.com/job_94a568bf-da54-41b2-bd7f-4fba373edc52/artifacts/v4ulre4i_967A5978-8886-4BEC-8D4C-307E87F33694.png)",
            filter: "brightness(1.5) contrast(1.04) saturate(1.1)",
            transform: "scaleX(-1)"
          }}
          data-testid="reviews-hero-bg"
        ></div>
        <div className="absolute inset-0 bg-gradient-to-r from-[#050505] via-[#050505]/65 to-[#050505]/30"></div>
        <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-transparent to-transparent"></div>
        <div className="container-custom relative z-10">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-8">
            <div>
              <p className="font-mono text-[#5b93ff] text-sm uppercase tracking-wider mb-2 font-semibold">Testimonials</p>
              <h1 className="font-heading font-black text-4xl md:text-5xl lg:text-6xl gradient-text mb-4">
                CUSTOMER REVIEWS
              </h1>
              <p className="text-gray-300 text-lg max-w-2xl">
                See what our customers have to say about their experience with Virgil's Heating and Air.
              </p>
            </div>
            
            {reviews.length > 0 && (
              <div className="bg-transparent p-8" data-testid="rating-summary">
                <div className="text-center [filter:drop-shadow(0_2px_8px_rgba(0,0,0,0.95))]">
                  <p className="font-heading font-black text-6xl gradient-text">{avgRating}</p>
                  <div className="flex gap-1 mt-3 justify-center">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <Star
                        key={star}
                        className={`w-6 h-6 ${
                          star <= Math.round(avgRating) 
                            ? 'text-yellow-400 fill-yellow-400' 
                            : 'text-gray-500'
                        }`}
                      />
                    ))}
                  </div>
                  <p className="text-white font-medium mt-2">{reviews.length} reviews</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Reviews Grid */}
      <section className="section-padding" data-testid="reviews-grid">
        <div className="container-custom">
          {reviews.length === 0 ? (
            <div className="text-center py-12">
              <MessageSquare className="w-16 h-16 text-gray-500 mx-auto mb-4" />
              <p className="text-gray-400 text-lg">No reviews yet. Be the first to leave a review!</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {reviews.map((review, index) => (
                <div
                  key={review.id}
                  className="bg-[#101014] border-2 border-white/10 hover:border-[#0066CC] rounded-2xl p-8 transition-all hover:shadow-xl relative"
                  data-testid={`review-card-${index}`}
                >
                  <Quote className="absolute top-6 right-6 w-10 h-10 text-white/5" />
                  
                  {/* Rating */}
                  <div className="flex gap-1 mb-4">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <Star
                        key={star}
                        className={`w-5 h-5 ${
                          star <= review.rating 
                            ? 'text-yellow-400 fill-yellow-400' 
                            : 'text-gray-700'
                        }`}
                      />
                    ))}
                  </div>

                  {/* Review Text */}
                  <p className="text-gray-300 mb-6 italic text-lg">"{review.review_text}"</p>

                  {/* Customer Info */}
                  <div className="flex items-center gap-3 border-t border-white/10 pt-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-[#0066CC] to-[#1E40AF] rounded-full flex items-center justify-center text-white font-bold text-lg">
                      {review.customer_name.charAt(0)}
                    </div>
                    <div>
                      <p className="font-heading font-bold text-white">{review.customer_name}</p>
                      <p className="text-[#5b93ff] text-sm">{review.service_type}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Submit Review Section */}
      <section className="section-padding bg-gradient-to-br from-[#0a0a12] to-[#0a0a0a]" data-testid="submit-review-section">
        <div className="container-custom">
          <div className="max-w-2xl mx-auto">
            {!showForm ? (
              <div className="text-center bg-[#101014] rounded-3xl p-12 shadow-xl">
                <MessageSquare className="w-16 h-16 text-[#5b93ff] mx-auto mb-6" />
                <h2 className="font-heading font-bold text-3xl gradient-text mb-4">
                  SHARE YOUR EXPERIENCE
                </h2>
                <p className="text-gray-400 text-lg mb-8">
                  Had a great experience with Virgil's? We'd love to hear from you!
                </p>
                <div className="flex flex-col sm:flex-row justify-center gap-4">
                  <Button 
                    onClick={() => setShowForm(true)} 
                    className="btn-primary text-lg px-10 py-6"
                    data-testid="write-review-btn"
                  >
                    Write a Review
                    <Star className="ml-2 w-5 h-5" />
                  </Button>
                  <a
                    href="https://share.google/IrL6h8FY38NM6LQjA"
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="google-review-btn"
                  >
                    <Button variant="outline" className="btn-outline text-lg px-10 py-6 w-full">
                      <svg className="mr-2 w-5 h-5" viewBox="0 0 48 48" aria-hidden="true">
                        <path fill="#FFC107" d="M43.611 20.083H42V20H24v8h11.303c-1.649 4.657-6.08 8-11.303 8-6.627 0-12-5.373-12-12s5.373-12 12-12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 12.955 4 4 12.955 4 24s8.955 20 20 20 20-8.955 20-20c0-1.341-.138-2.65-.389-3.917z"/>
                        <path fill="#FF3D00" d="m6.306 14.691 6.571 4.819C14.655 15.108 18.961 12 24 12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 16.318 4 9.656 8.337 6.306 14.691z"/>
                        <path fill="#4CAF50" d="M24 44c5.166 0 9.86-1.977 13.409-5.192l-6.19-5.238A11.91 11.91 0 0 1 24 36c-5.202 0-9.619-3.317-11.283-7.946l-6.522 5.025C9.505 39.556 16.227 44 24 44z"/>
                        <path fill="#1976D2" d="M43.611 20.083H42V20H24v8h11.303a12.04 12.04 0 0 1-4.087 5.571l.003-.002 6.19 5.238C36.971 39.205 44 34 44 24c0-1.341-.138-2.65-.389-3.917z"/>
                      </svg>
                      Review us on Google
                    </Button>
                  </a>
                </div>
              </div>
            ) : (
              <div className="bg-[#101014] rounded-3xl p-8 shadow-xl" data-testid="review-form">
                <h2 className="font-heading font-bold text-2xl text-white mb-6">
                  Leave a Review
                </h2>
                
                <form onSubmit={handleSubmit} className="space-y-6">
                  {/* Name */}
                  <div>
                    <Label className="text-white mb-2 block font-semibold">Your Name *</Label>
                    <Input
                      name="customer_name"
                      value={formData.customer_name}
                      onChange={handleInputChange}
                      placeholder="John D."
                      className="input-light h-14 text-lg"
                      data-testid="review-name-input"
                    />
                  </div>

                  {/* Service */}
                  <div>
                    <Label className="text-white mb-2 block font-semibold">Service Received *</Label>
                    <Select value={formData.service_type} onValueChange={handleServiceChange}>
                      <SelectTrigger className="input-light h-14 text-lg" data-testid="review-service-select">
                        <SelectValue placeholder="Select a service" />
                      </SelectTrigger>
                      <SelectContent className="bg-[#101014] border-white/10">
                        {services.map((service) => (
                          <SelectItem 
                            key={service.id} 
                            value={service.name}
                            className="text-white hover:bg-[#1E6BFF]/10 text-lg py-3"
                          >
                            {service.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Rating */}
                  <div>
                    <Label className="text-white mb-2 block font-semibold">Your Rating *</Label>
                    <div className="flex gap-3 bg-white/5 p-4 rounded-xl" data-testid="rating-selector">
                      {[1, 2, 3, 4, 5].map((star) => (
                        <button
                          key={star}
                          type="button"
                          onClick={() => handleRatingChange(star)}
                          className="p-2 transition-transform hover:scale-125"
                          data-testid={`star-${star}`}
                        >
                          <Star
                            className={`w-10 h-10 ${
                              star <= formData.rating 
                                ? 'text-yellow-400 fill-yellow-400' 
                                : 'text-gray-600 hover:text-yellow-300'
                            }`}
                          />
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Review Text */}
                  <div>
                    <Label className="text-white mb-2 block font-semibold">Your Review *</Label>
                    <Textarea
                      name="review_text"
                      value={formData.review_text}
                      onChange={handleInputChange}
                      placeholder="Tell us about your experience..."
                      className="input-light min-h-[150px] text-lg"
                      data-testid="review-text-input"
                    />
                  </div>

                  {/* Actions */}
                  <div className="flex gap-4">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setShowForm(false)}
                      className="btn-outline flex-1 py-6"
                      data-testid="cancel-review-btn"
                    >
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      disabled={isSubmitting}
                      className="btn-primary flex-1 py-6"
                      data-testid="submit-review-btn"
                    >
                      {isSubmitting ? "Submitting..." : "Submit Review"}
                      <Send className="ml-2 w-5 h-5" />
                    </Button>
                  </div>
                </form>
              </div>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
